import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Chip from '@mui/material/Chip';
import React from 'react';

import { neutral, petrol } from '../../styles/mui/colors';

/**
 * @param {object} props
 * @param {boolean} props.isVisible
 * @returns {React.JSX.Element}
 */
const IconCheck = ({ isVisible = false }) => {
  return isVisible ? <CheckCircleOutlineIcon /> : null;
};

const basicChipStyle = {
  borderRadius: '8px',
  px: 2,
  py: 3,
  mr: 2,
  mb: 2,
};
const chipSelectedStyle = {
  ...basicChipStyle,
  backgroundColor: neutral[100],
  color: petrol.main,
  border: `2px solid ${neutral[200]}`,
  borderRadius: '300px',
  '.MuiChip-icon': {
    color: petrol.main,
  },
};
const chipNotSelectedStyle = {
  ...basicChipStyle,
  backgroundColor: 'white',
  color: neutral[600],
  textDecoration: 'line-through',

  '.MuiChip-icon': {
    color: 'white',
  },
};

/**
 * `ItemChip` is a presentational component that renders a single chip representing an item.
 * It is designed to be used within the `PropertyGroup` component to display options like amenities,
 * activities, etc., each with a potentially associated icon and styling that reflects selection state.
 *
 * Props (to be detailed as the component implementation is fleshed out):
 * - `label`: The text to be displayed on the chip.
 * - `isSelected`: Boolean indicating if the chip is selected or not, which affects styling.
 * - `icon`: The icon component to be displayed alongside the label, if applicable.
 * - `onSelect`: Callback function that is invoked when the chip is selected.
 * - `onDeselect`: Callback function that is invoked when the chip is deselected.
 * - Additional props as required for styling and behavior.
 *
 * Usage:
 * ```
 * <ItemChip
 *   label="Amenity Name"
 *   isSelected={true/false}
 *   icon={<SomeIconComponent />}
 *   onSelect={() => {}}
 *   onDeselect={() => {}}
 *   ...otherProps
 * />
 * ```
 * @param {object} props
 * @param {string} props.label
 * @param {boolean} props.isSelected
 * @param {boolean} props.showNotSelectedItems
 * @param {React.ReactElement | null} props.icon
 * @returns {React.JSX.Element}
 */
const ItemChip = ({ label, isSelected, showNotSelectedItems = false, icon = null }) => {
  return isSelected || (!isSelected && showNotSelectedItems) ? (
    <Chip
      icon={icon || <IconCheck isVisible={isSelected} />}
      label={label}
      sx={!isSelected ? chipNotSelectedStyle : chipSelectedStyle}
    />
  ) : null;
};

export default ItemChip;
